import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, EyeIcon, EyeOffIcon, XCircle, ArrowLeftIcon, ListIcon, FileTextIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, CheckIcon, ClockIcon, PaperclipIcon, UploadIcon, DownloadIcon, Trash2Icon, FileIcon, Loader2Icon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { RichTextEditor } from '../components/RichTextEditor';
import { Customer } from '../components/CustomerCard';
import { Task, TaskAttachment } from '../components/TaskCard';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
interface TaskDetailProps {
  task: Task;
  customers: Customer[];
  onBack: () => void;
  onSave: (updatedTask: Task) => void;
  isReadOnly?: boolean;
}
export function TaskDetail({
  task,
  customers,
  onBack,
  onSave,
  isReadOnly = false
}: TaskDetailProps) {
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'todo' | 'in-progress' | 'done' | 'planned' | 'pending'>('in-progress');
  const [chargerType, setChargerType] = useState<'Zaptec 1' | 'Zaptec 2' | 'Andet'>('Zaptec 1');
  const [taskType, setTaskType] = useState<'Tilbudsopgave' | 'Regningsopgave'>('Tilbudsopgave');
  const [agreedPlanning, setAgreedPlanning] = useState('');
  const [agreedPlanningTime, setAgreedPlanningTime] = useState('09:00');
  const [agreedInstallation, setAgreedInstallation] = useState('');
  const [agreedInstallationTime, setAgreedInstallationTime] = useState('09:00');
  const [carDeliveryDate, setCarDeliveryDate] = useState('');
  const [hours, setHours] = useState('');
  const [materialPrice, setMaterialPrice] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [invoiced, setInvoiced] = useState('no');
  const [subTasks, setSubTasks] = useState('');
  const [materialInput, setMaterialInput] = useState('');
  const [materialPriceInput, setMaterialPriceInput] = useState('');
  const [materials, setMaterials] = useState<string[]>([]);
  const [showMaterials, setShowMaterials] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkInput, setBulkInput] = useState('');
  const [bulkPrice, setBulkPrice] = useState('');
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<number | null>(null);
  const [editingMaterialValue, setEditingMaterialValue] = useState('');
  const [workLogDate, setWorkLogDate] = useState(new Date().toISOString().split('T')[0]);
  const [workLogDescription, setWorkLogDescription] = useState('');
  const [workLogHours, setWorkLogHours] = useState('');
  const [workLog, setWorkLog] = useState<Array<{
    date: string;
    description: string;
    hours?: number;
  }>>([]);
  const [showWorkLog, setShowWorkLog] = useState(false);
  const [editingWorkLogIndex, setEditingWorkLogIndex] = useState<number | null>(null);
  const [editingWorkLogDate, setEditingWorkLogDate] = useState('');
  const [editingWorkLogDescription, setEditingWorkLogDescription] = useState('');
  const [editingWorkLogHours, setEditingWorkLogHours] = useState('');
  // Attachments state
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    pricing: true,
    materials: true,
    workLog: true,
    attachments: true
  });
  const [chargerPrices, setChargerPrices] = useState({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) setChargerPrices(docSnap.data() as any);
      } catch (error) {
        console.error('Error loading charger prices:', error);
      }
    };
    loadPrices();
  }, []);
  useEffect(() => {
    if (task?.category === 'Ladeboks' && chargerType !== 'Andet') {
      setMaterialPrice(chargerPrices[chargerType].toString());
    }
  }, [chargerType, task?.category, chargerPrices]);
  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setCustomerId(task.customerId);
      setDescription(task.description || '');
      setStatus(task.status);
      setChargerType(task.chargerType || 'Zaptec 1');
      setTaskType(task.taskType || 'Tilbudsopgave');
      if (task.agreedPlanning) {
        const [date, time] = task.agreedPlanning.split('T');
        setAgreedPlanning(date);
        setAgreedPlanningTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedPlanning('');
        setAgreedPlanningTime('09:00');
      }
      if (task.agreedInstallation) {
        const [date, time] = task.agreedInstallation.split('T');
        setAgreedInstallation(date);
        setAgreedInstallationTime(time ? time.substring(0, 5) : '09:00');
      } else {
        setAgreedInstallation('');
        setAgreedInstallationTime('09:00');
      }
      setCarDeliveryDate(task.carDeliveryDate || '');
      setHours(task.hours?.toString() || '');
      setMaterialPrice(task.materialPrice?.toString() || '');
      setHourlyRate(task.hourlyRate?.toString() || '');
      setQuotePrice(task.quotePrice?.toString() || '');
      setInvoiced(task.invoiced ? 'yes' : 'no');
      setSubTasks(task.subTasks || '');
      setMaterials(task.materials || []);
      setMaterialInput('');
      setMaterialPriceInput('');
      setBulkInput('');
      setBulkPrice('');
      setShowMaterials(false);
      setBulkMode(false);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
      setWorkLog(task.workLog || []);
      setWorkLogDate(new Date().toISOString().split('T')[0]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setShowWorkLog(false);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
      setAttachments(task.attachments || []);
    }
  }, [task]);
  const totalHoursWithWorkLog = useMemo(() => {
    const baseHours = hours ? parseFloat(hours) : 0;
    const workLogHoursTotal = workLog.reduce((sum, entry) => sum + (entry.hours || 0), 0);
    return baseHours + workLogHoursTotal;
  }, [hours, workLog]);
  const calculations = useMemo(() => {
    const material = materialPrice ? parseFloat(materialPrice) : 0;
    const rate = hourlyRate ? parseFloat(hourlyRate) : 0;
    const time = totalHoursWithWorkLog;
    const quote = quotePrice ? parseFloat(quotePrice) : 0;
    if (taskType === 'Tilbudsopgave') {
      const profit = quote - material;
      const totalLaborCost = rate * time;
      return {
        totalPrice: quote,
        profit: quote > 0 || material > 0 ? profit : null,
        totalLaborCost: rate > 0 && time > 0 ? totalLaborCost : null
      };
    } else {
      const laborCost = rate * time;
      const totalPrice = laborCost + material;
      const profit = laborCost - material;
      return {
        totalPrice,
        profit: rate > 0 && time > 0 || material > 0 ? profit : null,
        totalLaborCost: rate > 0 && time > 0 ? laborCost : null
      };
    }
  }, [taskType, quotePrice, materialPrice, hourlyRate, totalHoursWithWorkLog]);
  const handleAddMaterial = () => {
    if (materialInput.trim()) {
      setMaterials([...materials, materialInput.trim()]);
      if (materialPriceInput.trim()) {
        const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
        const additionalPrice = parseFloat(materialPriceInput);
        setMaterialPrice((currentPrice + additionalPrice).toString());
      }
      setMaterialInput('');
      setMaterialPriceInput('');
    }
  };
  const handleAddBulkMaterials = () => {
    if (bulkInput.trim()) {
      const newMaterials = bulkInput.split('\n').map(line => line.trim()).filter(line => line.length > 0);
      if (newMaterials.length > 0) {
        setMaterials([...materials, ...newMaterials]);
        if (bulkPrice.trim()) {
          const currentPrice = materialPrice ? parseFloat(materialPrice) : 0;
          setMaterialPrice((currentPrice + parseFloat(bulkPrice)).toString());
        }
        setBulkInput('');
        setBulkPrice('');
        setBulkMode(false);
      }
    }
  };
  const handleRemoveMaterial = (index: number) => setMaterials(materials.filter((_, i) => i !== index));
  const handleStartEditMaterial = (index: number) => {
    setEditingMaterialIndex(index);
    setEditingMaterialValue(materials[index]);
  };
  const handleSaveEditMaterial = () => {
    if (editingMaterialIndex !== null && editingMaterialValue.trim()) {
      const updatedMaterials = [...materials];
      updatedMaterials[editingMaterialIndex] = editingMaterialValue.trim();
      setMaterials(updatedMaterials);
      setEditingMaterialIndex(null);
      setEditingMaterialValue('');
    }
  };
  const handleCancelEditMaterial = () => {
    setEditingMaterialIndex(null);
    setEditingMaterialValue('');
  };
  const handleAddWorkLog = () => {
    if (workLogDescription.trim() && workLogDate) {
      const entry: any = {
        date: workLogDate,
        description: workLogDescription.trim()
      };
      if (workLogHours.trim()) entry.hours = parseFloat(workLogHours);
      setWorkLog([...workLog, entry]);
      setWorkLogDescription('');
      setWorkLogHours('');
      setWorkLogDate(new Date().toISOString().split('T')[0]);
    }
  };
  const handleRemoveWorkLog = (index: number) => setWorkLog(workLog.filter((_, i) => i !== index));
  const handleStartEditWorkLog = (index: number) => {
    const entry = workLog[index];
    setEditingWorkLogIndex(index);
    setEditingWorkLogDate(entry.date);
    setEditingWorkLogDescription(entry.description);
    setEditingWorkLogHours(entry.hours?.toString() || '');
  };
  const handleSaveEditWorkLog = () => {
    if (editingWorkLogIndex !== null && editingWorkLogDescription.trim() && editingWorkLogDate) {
      const updatedWorkLog = [...workLog];
      const entry: any = {
        date: editingWorkLogDate,
        description: editingWorkLogDescription.trim()
      };
      if (editingWorkLogHours.trim()) entry.hours = parseFloat(editingWorkLogHours);
      updatedWorkLog[editingWorkLogIndex] = entry;
      setWorkLog(updatedWorkLog);
      setEditingWorkLogIndex(null);
      setEditingWorkLogDate('');
      setEditingWorkLogDescription('');
      setEditingWorkLogHours('');
    }
  };
  const handleCancelEditWorkLog = () => {
    setEditingWorkLogIndex(null);
    setEditingWorkLogDate('');
    setEditingWorkLogDescription('');
    setEditingWorkLogHours('');
  };
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !task) return;
    try {
      setIsUploading(true);
      const storagePath = `tasks/${task.id}/bilag/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newAttachment: TaskAttachment = {
        name: file.name,
        url,
        storagePath,
        uploadedAt: new Date().toISOString(),
        size: file.size
      };
      setAttachments(prev => [...prev, newAttachment]);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Der opstod en fejl under upload af filen.');
    } finally {
      setIsUploading(false);
      // Reset input
      e.target.value = '';
    }
  };
  const handleDeleteAttachment = async (attachment: TaskAttachment, index: number) => {
    if (!confirm('Er du sikker på, at du vil slette dette bilag?')) return;
    try {
      const storageRef = ref(storage, attachment.storagePath);
      await deleteObject(storageRef);
      setAttachments(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Der opstod en fejl under sletning af filen.');
    }
  };
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  const toggleAllSections = () => {
    const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
    setExpandedSections({
      pricing: !allExpanded,
      materials: !allExpanded,
      workLog: !allExpanded,
      attachments: !allExpanded
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!task || !title.trim() || !customerId) return;
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    const planningDateTime = agreedPlanning ? `${agreedPlanning}T${agreedPlanningTime}:00` : undefined;
    const installationDateTime = agreedInstallation ? `${agreedInstallation}T${agreedInstallationTime}:00` : undefined;
    onSave({
      ...task,
      title,
      customerId,
      customerName: customer.name,
      description,
      status,
      chargerType: task.category === 'Ladeboks' ? chargerType : undefined,
      taskType,
      agreedPlanning: planningDateTime,
      agreedInstallation: installationDateTime,
      carDeliveryDate: carDeliveryDate || undefined,
      hours: hours ? parseFloat(hours) : undefined,
      materialPrice: materialPrice ? parseFloat(materialPrice) : undefined,
      hourlyRate: hourlyRate ? parseFloat(hourlyRate) : undefined,
      quotePrice: quotePrice ? parseFloat(quotePrice) : undefined,
      totalPrice: calculations.totalPrice || undefined,
      invoiced: invoiced === 'yes',
      subTasks: subTasks || undefined,
      materials: materials.length > 0 ? materials : undefined,
      workLog: workLog.length > 0 ? workLog : undefined,
      attachments: attachments.length > 0 ? attachments : undefined
    });
    onBack();
  };
  if (!task) return null;
  const allExpanded = expandedSections.pricing && expandedSections.materials && expandedSections.workLog && expandedSections.attachments;
  return <div className="w-full" data-id="element-1823">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      type: 'spring',
      duration: 0.3
    }} className="max-w-4xl mx-auto" data-id="element-1824">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200" data-id="element-1825">
          {/* Sticky header */}
          <div className="sticky top-0 z-10 bg-white border-b border-stone-200 rounded-t-2xl px-4 sm:px-6 py-4" data-id="element-1826">
            <div className="flex items-center justify-between" data-id="element-1827">
              <div className="flex items-center gap-4" data-id="element-1828">
                <button onClick={onBack} className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors" aria-label="Tilbage" data-id="element-1829">
                  <ArrowLeftIcon className="w-6 h-6" data-id="element-1830" />
                </button>
                <div data-id="element-1831">
                  <h2 className="text-xl sm:text-2xl font-semibold text-stone-900" data-id="element-1832">
                    Rediger opgave
                  </h2>
                  {task?.taskId && <p className="text-sm text-stone-500 mt-0.5" data-id="element-1833">
                      Opgave-ID:{' '}
                      <span className="font-medium text-teal-600" data-id="element-1834">
                        {task.taskId}
                      </span>
                    </p>}
                </div>
              </div>
              {!isReadOnly && <Button type="button" variant="primary" onClick={handleSubmit as any} className="hidden sm:flex" data-id="element-1835">
                  Gem ændringer
                </Button>}
            </div>
          </div>

          <div className="p-4 sm:p-6" data-id="element-1836">
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1837">
              {/* Basic Information */}
              <div data-id="element-1838">
                <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-1839">
                  Grundlæggende information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1840">
                  <Input label="Opgavetitel" value={title} onChange={e => setTitle(e.target.value)} placeholder="Indtast opgavetitel" required disabled={isReadOnly} data-id="element-1841" />

                  <Select label="Kunde" value={customerId} onChange={e => setCustomerId(e.target.value)} options={[{
                  value: '',
                  label: 'Vælg en kunde'
                }, ...customers.map(c => ({
                  value: c.id,
                  label: `${c.name} (${c.customerId})`
                }))]} required disabled={isReadOnly} data-id="element-1842" />
                </div>

                <div className="mt-4" data-id="element-1843">
                  <RichTextEditor label="Beskrivelse" value={description} onChange={setDescription} placeholder="Beskriv opgaven" rows={3} disabled={isReadOnly} data-id="element-1844" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-1845">
                  <Select label="Status" value={status} onChange={e => setStatus(e.target.value as 'todo' | 'in-progress' | 'done' | 'planned' | 'pending')} options={[{
                  value: 'planned',
                  label: 'Planlagt'
                }, {
                  value: 'pending',
                  label: 'Afventende'
                }, {
                  value: 'in-progress',
                  label: 'I gang'
                }, {
                  value: 'done',
                  label: 'Afsluttet'
                }]} disabled={isReadOnly} data-id="element-1846" />

                  <Select label="Faktureret" value={invoiced} onChange={e => setInvoiced(e.target.value)} options={[{
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'yes',
                  label: 'Ja'
                }]} disabled={isReadOnly} data-id="element-1847" />
                </div>
              </div>

              {/* Dates */}
              <div data-id="element-1848">
                <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-1849">
                  Datoer
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1850">
                  <div data-id="element-1851">
                    <Input label="Aftalt projektering" type="date" value={agreedPlanning} onChange={e => setAgreedPlanning(e.target.value)} disabled={isReadOnly} data-id="element-1852" />
                    {agreedPlanning && <div className="mt-2" data-id="element-1853">
                        <Input label="Tidspunkt" type="time" value={agreedPlanningTime} onChange={e => setAgreedPlanningTime(e.target.value)} disabled={isReadOnly} data-id="element-1854" />
                      </div>}
                  </div>

                  <div data-id="element-1855">
                    <Input label="Aftalt installation" type="date" value={agreedInstallation} onChange={e => setAgreedInstallation(e.target.value)} disabled={isReadOnly} data-id="element-1856" />
                    {agreedInstallation && <div className="mt-2" data-id="element-1857">
                        <Input label="Tidspunkt" type="time" value={agreedInstallationTime} onChange={e => setAgreedInstallationTime(e.target.value)} disabled={isReadOnly} data-id="element-1858" />
                      </div>}
                  </div>
                </div>

                {task.category === 'Ladeboks' && <div className="mt-4" data-id="element-1859">
                    <Input label="Leveringsdato på bil (valgfrit)" type="date" value={carDeliveryDate} onChange={e => setCarDeliveryDate(e.target.value)} disabled={isReadOnly} data-id="element-1860" />
                    <p className="text-xs text-stone-500 mt-1" data-id="element-1861">
                      Angiv hvornår bilen leveres til kunden
                    </p>
                  </div>}
              </div>

              {/* Expand/Collapse All Button */}
              {!isReadOnly && <div className="flex justify-end" data-id="element-1862">
                  <button type="button" onClick={toggleAllSections} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors" data-id="element-1863">
                    {allExpanded ? <>
                        <ChevronUpIcon className="w-4 h-4" data-id="element-1864" />
                        Skjul alle sektioner
                      </> : <>
                        <ChevronDownIcon className="w-4 h-4" data-id="element-1865" />
                        Udvid alle sektioner
                      </>}
                  </button>
                </div>}

              {/* PRICING SECTION */}
              <AnimatePresence data-id="element-1866">
                {expandedSections.pricing ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1867">
                    <div data-id="element-1868">
                      <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    pricing: false
                  })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-1869">
                        <h3 className="text-lg font-semibold text-stone-900" data-id="element-1870">
                          Priser og timer
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-1871">
                          {totalHoursWithWorkLog > 0 && <span className="text-sm text-stone-500" data-id="element-1872">
                              Total: {totalHoursWithWorkLog.toFixed(1)}t
                            </span>}
                          <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-1873" />
                        </div>
                      </button>

                      <div className="space-y-4" data-id="element-1874">
                        {task.category === 'Ladeboks' && <Select label="Ladeboks type" value={chargerType} onChange={e => setChargerType(e.target.value as 'Zaptec 1' | 'Zaptec 2' | 'Andet')} options={[{
                      value: 'Zaptec 1',
                      label: 'Zaptec 1'
                    }, {
                      value: 'Zaptec 2',
                      label: 'Zaptec 2'
                    }, {
                      value: 'Andet',
                      label: 'Andet'
                    }]} required disabled={isReadOnly} data-id="element-1875" />}

                        <Select label="Opgavetype" value={taskType} onChange={e => setTaskType(e.target.value as 'Tilbudsopgave' | 'Regningsopgave')} options={[{
                      value: 'Tilbudsopgave',
                      label: 'Tilbudsopgave'
                    }, {
                      value: 'Regningsopgave',
                      label: 'Regningsopgave'
                    }]} required disabled={isReadOnly} data-id="element-1876" />

                        {taskType === 'Tilbudsopgave' ? <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1877">
                              <Input label="Tilbudspris (kr)" type="number" step="0.01" value={quotePrice} onChange={e => setQuotePrice(e.target.value)} placeholder="f.eks. 8000" required disabled={isReadOnly} data-id="element-1878" />
                              <Input label="Materialer pris (kr)" type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={isReadOnly || task.category === 'Ladeboks' && chargerType !== 'Andet'} data-id="element-1879" />
                            </div>

                            {task.category === 'Ladeboks' && chargerType !== 'Andet' && <p className="text-xs text-stone-500 mt-2" data-id="element-1880">
                                  Materialer pris udfyldes automatisk baseret på
                                  ladeboks type
                                </p>}

                            <div className="mt-4" data-id="element-1881">
                              <Input label="Timepris (kr)" type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" disabled={isReadOnly} data-id="element-1882" />
                            </div>

                            <div className="mt-4" data-id="element-1883">
                              <Input label="Timer brugt" type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" disabled={isReadOnly} data-id="element-1884" />
                            </div>

                            <div className="mt-4 space-y-3" data-id="element-1885">
                              {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-1886">
                                  <div className="flex items-center justify-between" data-id="element-1887">
                                    <span className="text-sm font-medium text-teal-900" data-id="element-1888">
                                      Fortjeneste
                                    </span>
                                    <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-1889">
                                      {calculations.profit.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                      kr
                                    </span>
                                  </div>
                                </div>}

                              {calculations.totalLaborCost !== null && calculations.totalLaborCost !== undefined && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1890">
                                    <div className="flex items-center justify-between" data-id="element-1891">
                                      <span className="text-sm font-medium text-blue-900" data-id="element-1892">
                                        Samlet pris på timer
                                      </span>
                                      <span className="text-lg font-bold text-blue-700" data-id="element-1893">
                                        {calculations.totalLaborCost.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-1" data-id="element-1894">
                                      Timepris × Timer brugt
                                    </p>
                                  </div>}
                            </div>
                          </> : <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1895">
                              <Input label="Timepris (kr)" type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder="f.eks. 750" required disabled={isReadOnly} data-id="element-1896" />
                              <Input label="Timer brugt" type="number" step="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="f.eks. 4" required disabled={isReadOnly} data-id="element-1897" />
                              <Input label="Materialer pris (kr)" type="number" step="0.01" value={materialPrice} onChange={e => setMaterialPrice(e.target.value)} placeholder="f.eks. 4400" disabled={isReadOnly || task.category === 'Ladeboks' && chargerType !== 'Andet'} data-id="element-1898" />
                            </div>

                            {task.category === 'Ladeboks' && chargerType !== 'Andet' && <p className="text-xs text-stone-500 mt-2" data-id="element-1899">
                                  Materialer pris udfyldes automatisk baseret på
                                  ladeboks type
                                </p>}

                            <div className="mt-4 space-y-3" data-id="element-1900">
                              {calculations.totalLaborCost !== null && calculations.totalLaborCost !== undefined && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1901">
                                    <div className="flex items-center justify-between" data-id="element-1902">
                                      <span className="text-sm font-medium text-blue-900" data-id="element-1903">
                                        Samlet pris på timer
                                      </span>
                                      <span className="text-lg font-bold text-blue-700" data-id="element-1904">
                                        {calculations.totalLaborCost.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                        kr
                                      </span>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-1" data-id="element-1905">
                                      Timepris × Timer brugt
                                    </p>
                                  </div>}

                              <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg" data-id="element-1906">
                                <div className="flex items-center justify-between" data-id="element-1907">
                                  <span className="text-sm font-medium text-stone-700" data-id="element-1908">
                                    Samlet pris
                                  </span>
                                  <span className="text-lg font-bold text-stone-900" data-id="element-1909">
                                    {calculations.totalPrice.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                    kr
                                  </span>
                                </div>
                              </div>

                              {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-1910">
                                  <div className="flex items-center justify-between" data-id="element-1911">
                                    <span className="text-sm font-medium text-teal-900" data-id="element-1912">
                                      Fortjeneste
                                    </span>
                                    <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-1913">
                                      {calculations.profit.toLocaleString('da-DK', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}{' '}
                                      kr
                                    </span>
                                  </div>
                                </div>}
                            </div>
                          </>}
                      </div>
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1914">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  pricing: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-1915">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-1916" />
                      Vis Priser og timer
                      {totalHoursWithWorkLog > 0 && <span className="text-stone-500" data-id="element-1917">
                          (Total: {totalHoursWithWorkLog.toFixed(1)}t)
                        </span>}
                    </button>
                  </motion.div>}
              </AnimatePresence>

              {/* MATERIALS SECTION */}
              <AnimatePresence data-id="element-1918">
                {expandedSections.materials ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1919">
                    <div data-id="element-1920">
                      <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    materials: false
                  })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-1921">
                        <h3 className="text-lg font-semibold text-stone-900" data-id="element-1922">
                          Materialer
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-1923">
                          {materials.length > 0 && <span className="text-sm text-stone-500" data-id="element-1924">
                              {materials.length} materialer
                            </span>}
                          <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-1925" />
                        </div>
                      </button>

                      <div className="space-y-3" data-id="element-1926">
                        {!isReadOnly && <>
                            <div className="flex items-center justify-between mb-4" data-id="element-1927">
                              <div className="flex-1" data-id="element-1928" />
                              <button type="button" onClick={() => setBulkMode(!bulkMode)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors" data-id="element-1929">
                                <ListIcon className="w-4 h-4" data-id="element-1930" />
                                {bulkMode ? 'Enkelt tilføjelse' : 'Tilføj ordre'}
                              </button>
                            </div>

                            <AnimatePresence mode="wait" data-id="element-1931">
                              {bulkMode ? <motion.div key="bulk" initial={{
                          opacity: 0,
                          y: -10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -10
                        }} transition={{
                          duration: 0.2
                        }} className="space-y-3" data-id="element-1932">
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-1933">
                                    <p className="text-sm text-blue-900 font-medium mb-1" data-id="element-1934">
                                      📦 Tilføj stor ordre
                                    </p>
                                    <p className="text-xs text-blue-700" data-id="element-1935">
                                      Skriv ét materiale per linje. Prisen
                                      lægges automatisk til materialer pris.
                                    </p>
                                  </div>

                                  <textarea value={bulkInput} onChange={e => setBulkInput(e.target.value)} placeholder="Kabel 10m&#10;Stikkontakt&#10;Sikring 16A&#10;Kabelkanal 2m&#10;..." rows={6} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none font-mono text-sm" data-id="element-1936" />

                                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-1937">
                                    <label className="block text-sm font-medium text-amber-900 mb-2" data-id="element-1938">
                                      💰 Pris for denne ordre (kr)
                                    </label>
                                    <input type="number" step="0.01" value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="f.eks. 2500" className="w-full px-4 py-2.5 rounded-lg border border-amber-300 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all" data-id="element-1939" />
                                    {bulkPrice && materialPrice && <div className="mt-3 pt-3 border-t border-amber-200" data-id="element-1940">
                                        <div className="flex items-center justify-between text-sm" data-id="element-1941">
                                          <span className="text-amber-700" data-id="element-1942">
                                            Nuværende materialer pris:
                                          </span>
                                          <span className="font-medium text-amber-900" data-id="element-1943">
                                            {parseFloat(materialPrice).toLocaleString('da-DK', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}{' '}
                                            kr
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm mt-1" data-id="element-1944">
                                          <span className="text-amber-700" data-id="element-1945">
                                            Ordre pris:
                                          </span>
                                          <span className="font-medium text-amber-900" data-id="element-1946">
                                            +
                                            {parseFloat(bulkPrice).toLocaleString('da-DK', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}{' '}
                                            kr
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between text-base mt-2 pt-2 border-t border-amber-300" data-id="element-1947">
                                          <span className="font-semibold text-amber-900" data-id="element-1948">
                                            Ny total:
                                          </span>
                                          <span className="font-bold text-amber-900" data-id="element-1949">
                                            {(parseFloat(materialPrice) + parseFloat(bulkPrice)).toLocaleString('da-DK', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}{' '}
                                            kr
                                          </span>
                                        </div>
                                      </div>}
                                  </div>

                                  <div className="flex gap-2" data-id="element-1950">
                                    <Button type="button" onClick={handleAddBulkMaterials} variant="primary" className="flex-1 flex items-center justify-center gap-2" disabled={!bulkInput.trim()} data-id="element-1951">
                                      <PlusIcon className="w-4 h-4" data-id="element-1952" />
                                      Tilføj alle materialer
                                      {bulkPrice && ` (+${parseFloat(bulkPrice).toLocaleString('da-DK')} kr)`}
                                    </Button>
                                    <Button type="button" onClick={() => {
                              setBulkInput('');
                              setBulkPrice('');
                              setBulkMode(false);
                            }} variant="secondary" data-id="element-1953">
                                      Annuller
                                    </Button>
                                  </div>
                                </motion.div> : <motion.div key="single" initial={{
                          opacity: 0,
                          y: -10
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -10
                        }} transition={{
                          duration: 0.2
                        }} className="space-y-3" data-id="element-1954">
                                  <div className="flex gap-2" data-id="element-1955">
                                    <input type="text" value={materialInput} onChange={e => setMaterialInput(e.target.value)} onKeyPress={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddMaterial();
                              }
                            }} placeholder="Skriv materiale navn..." className="flex-1 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1956" />
                                    <input type="number" step="0.01" value={materialPriceInput} onChange={e => setMaterialPriceInput(e.target.value)} onKeyPress={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddMaterial();
                              }
                            }} placeholder="Pris (kr)" className="w-32 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1957" />
                                    <Button type="button" onClick={handleAddMaterial} variant="primary" className="flex items-center gap-2" disabled={!materialInput.trim()} data-id="element-1958">
                                      <PlusIcon className="w-4 h-4" data-id="element-1959" />
                                      Tilføj
                                    </Button>
                                  </div>

                                  {materialPriceInput && materialPrice && <motion.div initial={{
                            opacity: 0,
                            height: 0
                          }} animate={{
                            opacity: 1,
                            height: 'auto'
                          }} exit={{
                            opacity: 0,
                            height: 0
                          }} transition={{
                            duration: 0.2
                          }} className="bg-green-50 border border-green-200 rounded-lg p-3" data-id="element-1960">
                                      <div className="flex items-center justify-between text-sm" data-id="element-1961">
                                        <span className="text-green-700" data-id="element-1962">
                                          Nuværende total:
                                        </span>
                                        <span className="font-medium text-green-900" data-id="element-1963">
                                          {parseFloat(materialPrice).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-sm mt-1" data-id="element-1964">
                                        <span className="text-green-700" data-id="element-1965">
                                          Tilføjes:
                                        </span>
                                        <span className="font-medium text-green-900" data-id="element-1966">
                                          +
                                          {parseFloat(materialPriceInput).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-green-300" data-id="element-1967">
                                        <span className="font-semibold text-green-900" data-id="element-1968">
                                          Ny total:
                                        </span>
                                        <span className="font-bold text-green-900" data-id="element-1969">
                                          {(parseFloat(materialPrice) + parseFloat(materialPriceInput)).toLocaleString('da-DK', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}{' '}
                                          kr
                                        </span>
                                      </div>
                                    </motion.div>}
                                </motion.div>}
                            </AnimatePresence>
                          </>}

                        {materials.length > 0 && <div className="space-y-2 mt-4" data-id="element-1970">
                            <Button type="button" onClick={() => setShowMaterials(!showMaterials)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-1971">
                              {showMaterials ? <>
                                  <EyeOffIcon className="w-4 h-4" data-id="element-1972" />
                                  Skjul materialer ({materials.length})
                                </> : <>
                                  <EyeIcon className="w-4 h-4" data-id="element-1973" />
                                  Se materialer ({materials.length})
                                </>}
                            </Button>

                            <AnimatePresence data-id="element-1974">
                              {showMaterials && <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2
                        }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-1975">
                                  <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-1976">
                                    {materials.map((material, index) => <motion.li key={index} initial={{
                              opacity: 0,
                              x: -10
                            }} animate={{
                              opacity: 1,
                              x: 0
                            }} exit={{
                              opacity: 0,
                              x: -10
                            }} transition={{
                              delay: index * 0.02
                            }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-1977">
                                        {editingMaterialIndex === index ? <div className="flex items-center gap-2" data-id="element-1978">
                                            <input type="text" value={editingMaterialValue} onChange={e => setEditingMaterialValue(e.target.value)} onKeyPress={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveEditMaterial();
                                  } else if (e.key === 'Escape') {
                                    handleCancelEditMaterial();
                                  }
                                }} className="flex-1 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" autoFocus data-id="element-1979" />
                                            <button type="button" onClick={handleSaveEditMaterial} className="p-1.5 text-teal-600 hover:bg-teal-50 rounded transition-colors" data-id="element-1980">
                                              <CheckIcon className="w-4 h-4" data-id="element-1981" />
                                            </button>
                                            <button type="button" onClick={handleCancelEditMaterial} className="p-1.5 text-stone-400 hover:bg-stone-100 rounded transition-colors" data-id="element-1982">
                                              <XCircle className="w-4 h-4" data-id="element-1983" />
                                            </button>
                                          </div> : <div className="flex items-center justify-between" data-id="element-1984">
                                            <span className="text-sm text-stone-900" data-id="element-1985">
                                              {material}
                                            </span>
                                            {!isReadOnly && <div className="flex items-center gap-1" data-id="element-1986">
                                                <button type="button" onClick={() => handleStartEditMaterial(index)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger materiale" data-id="element-1987">
                                                  <EditIcon className="w-4 h-4" data-id="element-1988" />
                                                </button>
                                                <button type="button" onClick={() => handleRemoveMaterial(index)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern materiale" data-id="element-1989">
                                                  <XCircle className="w-4 h-4" data-id="element-1990" />
                                                </button>
                                              </div>}
                                          </div>}
                                      </motion.li>)}
                                  </ul>
                                </motion.div>}
                            </AnimatePresence>
                          </div>}

                        {materials.length === 0 && !bulkMode && <p className="text-sm text-stone-500 text-center py-4 bg-stone-50 rounded-lg border border-stone-200 mt-3" data-id="element-1991">
                            Ingen materialer tilføjet endnu
                          </p>}
                      </div>
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1992">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  materials: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-1993">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-1994" />
                      Vis Materialer
                      {materials.length > 0 && <span className="text-stone-500" data-id="element-1995">
                          ({materials.length} materialer)
                        </span>}
                    </button>
                  </motion.div>}
              </AnimatePresence>

              {/* WORK LOG SECTION */}
              <AnimatePresence data-id="element-1996">
                {expandedSections.workLog ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-1997">
                    <div data-id="element-1998">
                      <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    workLog: false
                  })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-1999">
                        <h3 className="text-lg font-semibold text-stone-900" data-id="element-2000">
                          Arbejdslog
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-2001">
                          {workLog.length > 0 && <span className="text-sm text-stone-500" data-id="element-2002">
                              {workLog.length} entries
                            </span>}
                          <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2003" />
                        </div>
                      </button>

                      <div className="space-y-3" data-id="element-2004">
                        {!isReadOnly && <div className="space-y-3" data-id="element-2005">
                            <div className="flex gap-2" data-id="element-2006">
                              <input type="date" value={workLogDate} onChange={e => setWorkLogDate(e.target.value)} className="w-40 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2007" />
                              <input type="number" step="0.5" value={workLogHours} onChange={e => setWorkLogHours(e.target.value)} placeholder="Timer" className="w-24 px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-2008" />
                            </div>
                            <textarea value={workLogDescription} onChange={e => setWorkLogDescription(e.target.value)} placeholder="Beskriv hvad der blev lavet...&#10;Du kan skrive flere linjer her." rows={4} className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none" data-id="element-2009" />
                            <Button type="button" onClick={handleAddWorkLog} variant="primary" className="flex items-center gap-2 w-full justify-center" disabled={!workLogDescription.trim()} data-id="element-2010">
                              <PlusIcon className="w-4 h-4" data-id="element-2011" />
                              Tilføj til arbejdslog
                            </Button>
                          </div>}

                        {workLog.length > 0 && <div className="space-y-2 mt-4" data-id="element-2012">
                            <Button type="button" onClick={() => setShowWorkLog(!showWorkLog)} variant="secondary" className="flex items-center gap-2 w-full justify-center" data-id="element-2013">
                              {showWorkLog ? <>
                                  <EyeOffIcon className="w-4 h-4" data-id="element-2014" />
                                  Skjul arbejdslog ({workLog.length})
                                </> : <>
                                  <FileTextIcon className="w-4 h-4" data-id="element-2015" />
                                  Se arbejdslog ({workLog.length})
                                </>}
                            </Button>

                            <AnimatePresence data-id="element-2016">
                              {showWorkLog && <motion.div initial={{
                          opacity: 0,
                          height: 0
                        }} animate={{
                          opacity: 1,
                          height: 'auto'
                        }} exit={{
                          opacity: 0,
                          height: 0
                        }} transition={{
                          duration: 0.2
                        }} className="bg-stone-50 rounded-lg p-4 border border-stone-200" data-id="element-2017">
                                  <ul className="space-y-2 max-h-60 overflow-y-auto" data-id="element-2018">
                                    {workLog.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((entry, index) => {
                              const originalIndex = workLog.findIndex(e => e === entry);
                              return <motion.li key={originalIndex} initial={{
                                opacity: 0,
                                x: -10
                              }} animate={{
                                opacity: 1,
                                x: 0
                              }} exit={{
                                opacity: 0,
                                x: -10
                              }} transition={{
                                delay: index * 0.02
                              }} className="bg-white p-3 rounded-lg border border-stone-200" data-id="element-2019">
                                            {editingWorkLogIndex === originalIndex ? <div className="space-y-2" data-id="element-2020">
                                                <div className="flex gap-2" data-id="element-2021">
                                                  <input type="date" value={editingWorkLogDate} onChange={e => setEditingWorkLogDate(e.target.value)} className="w-40 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2022" />
                                                  <input type="number" step="0.5" value={editingWorkLogHours} onChange={e => setEditingWorkLogHours(e.target.value)} placeholder="Timer" className="w-24 px-3 py-1.5 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-2023" />
                                                </div>
                                                <textarea value={editingWorkLogDescription} onChange={e => setEditingWorkLogDescription(e.target.value)} rows={3} className="w-full px-3 py-2 text-sm rounded border border-teal-300 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" autoFocus data-id="element-2024" />
                                                <div className="flex gap-2" data-id="element-2025">
                                                  <button type="button" onClick={handleSaveEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-white bg-teal-600 hover:bg-teal-700 rounded transition-colors" data-id="element-2026">
                                                    Gem
                                                  </button>
                                                  <button type="button" onClick={handleCancelEditWorkLog} className="flex-1 px-3 py-1.5 text-sm text-stone-600 bg-stone-100 hover:bg-stone-200 rounded transition-colors" data-id="element-2027">
                                                    Annuller
                                                  </button>
                                                </div>
                                              </div> : <div className="flex items-start justify-between gap-3" data-id="element-2028">
                                                <div className="flex-1" data-id="element-2029">
                                                  <div className="flex items-center gap-2 mb-1" data-id="element-2030">
                                                    <CalendarIcon className="w-3.5 h-3.5 text-teal-600" data-id="element-2031" />
                                                    <span className="text-xs font-medium text-teal-700" data-id="element-2032">
                                                      {new Date(entry.date).toLocaleDateString('da-DK', {
                                          weekday: 'short',
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric'
                                        })}
                                                    </span>
                                                    {entry.hours && <>
                                                        <span className="text-xs text-stone-400" data-id="element-2033">
                                                          •
                                                        </span>
                                                        <ClockIcon className="w-3.5 h-3.5 text-blue-600" data-id="element-2034" />
                                                        <span className="text-xs font-medium text-blue-700" data-id="element-2035">
                                                          {entry.hours}t
                                                        </span>
                                                      </>}
                                                  </div>
                                                  <p className="text-sm text-stone-900" data-id="element-2036">
                                                    {entry.description}
                                                  </p>
                                                </div>
                                                {!isReadOnly && <div className="flex items-center gap-1 flex-shrink-0" data-id="element-2037">
                                                    <button type="button" onClick={() => handleStartEditWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors" aria-label="Rediger log" data-id="element-2038">
                                                      <EditIcon className="w-4 h-4" data-id="element-2039" />
                                                    </button>
                                                    <button type="button" onClick={() => handleRemoveWorkLog(originalIndex)} className="p-1 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label="Fjern log" data-id="element-2040">
                                                      <XCircle className="w-4 h-4" data-id="element-2041" />
                                                    </button>
                                                  </div>}
                                              </div>}
                                          </motion.li>;
                            })}
                                  </ul>
                                </motion.div>}
                            </AnimatePresence>
                          </div>}
                      </div>
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-2042">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  workLog: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2043">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-2044" />
                      Vis Arbejdslog
                      {workLog.length > 0 && <span className="text-stone-500" data-id="element-2045">
                          ({workLog.length} entries)
                        </span>}
                    </button>
                  </motion.div>}
              </AnimatePresence>

              {/* ATTACHMENTS SECTION */}
              <AnimatePresence data-id="element-2046">
                {expandedSections.attachments ? <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-2047">
                    <div data-id="element-2048">
                      <button type="button" onClick={() => setExpandedSections({
                    ...expandedSections,
                    attachments: false
                  })} className="flex items-center justify-between w-full mb-4 group" disabled={isReadOnly} data-id="element-2049">
                        <h3 className="text-lg font-semibold text-stone-900" data-id="element-2050">
                          Bilag
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-2051">
                          {attachments.length > 0 && <span className="text-sm text-stone-500" data-id="element-2052">
                              {attachments.length} bilag
                            </span>}
                          <ChevronUpIcon className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" data-id="element-2053" />
                        </div>
                      </button>

                      <div className="space-y-3" data-id="element-2054">
                        {!isReadOnly && <div className="flex justify-end mb-4" data-id="element-2055">
                            <input type="file" id="file-upload" className="hidden" onChange={handleFileUpload} disabled={isUploading} data-id="element-2056" />
                            <label htmlFor="file-upload" className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors cursor-pointer ${isUploading ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-2057">
                              {isUploading ? <>
                                  <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2058" />
                                  Uploader...
                                </> : <>
                                  <UploadIcon className="w-4 h-4" data-id="element-2059" />
                                  Tilføj bilag
                                </>}
                            </label>
                          </div>}

                        {attachments.length > 0 ? <ul className="space-y-2" data-id="element-2060">
                            {attachments.map((attachment, index) => <motion.li key={attachment.storagePath} initial={{
                        opacity: 0,
                        y: -10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} className="flex items-center justify-between bg-white p-3 rounded-lg border border-stone-200" data-id="element-2061">
                                <div className="flex items-center gap-3 overflow-hidden" data-id="element-2062">
                                  <div className="p-2 bg-stone-100 rounded-lg flex-shrink-0" data-id="element-2063">
                                    <FileIcon className="w-5 h-5 text-stone-600" data-id="element-2064" />
                                  </div>
                                  <div className="min-w-0" data-id="element-2065">
                                    <p className="text-sm font-medium text-stone-900 truncate" data-id="element-2066">
                                      {attachment.name}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-stone-500" data-id="element-2067">
                                      <span data-id="element-2068">
                                        {new Date(attachment.uploadedAt).toLocaleDateString('da-DK', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                                      </span>
                                      {attachment.size && <>
                                          <span data-id="element-2069">•</span>
                                          <span data-id="element-2070">
                                            {formatFileSize(attachment.size)}
                                          </span>
                                        </>}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0 ml-4" data-id="element-2071">
                                  <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="p-2 text-stone-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Download" data-id="element-2072">
                                    <DownloadIcon className="w-4 h-4" data-id="element-2073" />
                                  </a>
                                  {!isReadOnly && <button type="button" onClick={() => handleDeleteAttachment(attachment, index)} className="p-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Slet" data-id="element-2074">
                                      <Trash2Icon className="w-4 h-4" data-id="element-2075" />
                                    </button>}
                                </div>
                              </motion.li>)}
                          </ul> : <div className="text-center py-8 bg-stone-50 rounded-lg border border-stone-200" data-id="element-2076">
                            <PaperclipIcon className="w-8 h-8 text-stone-400 mx-auto mb-2" data-id="element-2077" />
                            <p className="text-sm text-stone-500" data-id="element-2078">
                              Ingen bilag tilføjet endnu
                            </p>
                          </div>}
                      </div>
                    </div>
                  </motion.div> : <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-2079">
                    <button type="button" onClick={() => setExpandedSections({
                  ...expandedSections,
                  attachments: true
                })} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-50 hover:bg-stone-100 rounded-lg transition-colors w-full justify-center" data-id="element-2080">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-2081" />
                      Vis Bilag
                      {attachments.length > 0 && <span className="text-stone-500" data-id="element-2082">
                          ({attachments.length} bilag)
                        </span>}
                    </button>
                  </motion.div>}
              </AnimatePresence>

              {/* Vigtige bemærkninger */}
              <div data-id="element-2083">
                <RichTextEditor label="Vigtige bemærkninger" value={subTasks} onChange={setSubTasks} placeholder="Vigtige bemærkninger om opgaven" rows={3} disabled={isReadOnly} data-id="element-2084" />
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2085">
                {/* Fortjeneste */}
                {calculations.profit !== null && <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-2086">
                    <div className="flex items-center justify-between" data-id="element-2087">
                      <span className="text-sm font-medium text-teal-900" data-id="element-2088">
                        Fortjeneste
                      </span>
                      <span className={`text-lg font-bold ${calculations.profit >= 0 ? 'text-teal-700' : 'text-red-700'}`} data-id="element-2089">
                        {calculations.profit.toLocaleString('da-DK', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}{' '}
                        kr
                      </span>
                    </div>
                  </div>}

                {/* Timer brugt i alt */}
                {totalHoursWithWorkLog > 0 && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-2090">
                    <div className="flex items-center justify-between" data-id="element-2091">
                      <span className="text-sm font-medium text-blue-900" data-id="element-2092">
                        Timer brugt i alt
                      </span>
                      <span className="text-lg font-bold text-blue-700" data-id="element-2093">
                        {totalHoursWithWorkLog.toFixed(1)} timer
                      </span>
                    </div>
                  </div>}
              </div>
            </form>
          </div>

          {/* Sticky bottom buttons */}
          <div className="sticky bottom-0 z-10 bg-white border-t border-stone-200 rounded-b-2xl px-4 sm:px-6 py-4" data-id="element-2094">
            <div className="flex gap-3" data-id="element-2095">
              <Button type="button" variant="secondary" onClick={onBack} className="flex-1" data-id="element-2096">
                Annuller
              </Button>
              <Button type="button" variant="primary" onClick={handleSubmit as any} className="flex-1" disabled={isReadOnly} data-id="element-2097">
                Gem ændringer
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}